import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import Modal from 'components/layouts/modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from 'components/core/text-input';
import TextArea from 'components/core/text-area';
import PermissionPicker from 'containers/permission-picker';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';
import { Role } from 'types/role';

interface UpdateRoleProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  role?: Role;
  onSuccess: () => void;
}

const UpdateRole: FC<UpdateRoleProps> = ({ open, setOpen, role, onSuccess }) => {
  const api = useApi();
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: role?.name || '',
      description: role?.description || '',
      permissions: role?.permissions || [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      description: Yup.string().optional(),
      permissions: Yup.array().of(Yup.string()).min(1, 'Select at least one permission'),
    }),
    onSubmit: async values => {
      if (!role?.id) return;
      await updateRoleMutation.mutateAsync(values);
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const res = await api.put(`/roles/${role?.id}`, values);
      return res.data;
    },
    onSuccess: () => {
      onSuccess();
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Edit Role"
      description=""
      size="5xl"
      renderActions={() => (
        <button
          type="button"
          onClick={wrapClick(() => form.handleSubmit())}
          disabled={updateRoleMutation.isPending}
          className="inline-flex justify-center rounded-md ml-3 border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
        >
          {updateRoleMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      )}
    >
      <div className="space-y-4">
        <TextInput id="name" label="Role Name" required {...form} />
        <TextArea id="description" label="Description" rows={3} {...form} />
        <PermissionPicker id="permissions" required {...form} />
      </div>
    </Modal>
  );
};

export default UpdateRole;
